/**
 * Watchdog · API-Typen fuer Domain-Anlage und Discovery (Datenmodell v0.1).
 * Geteilt zwischen Backend-Controller und Client.
 */

export type OnboardingStatus = 'new' | 'discovery_running' | 'active' | 'failed';
export type DomainStatus = 'active' | 'paused' | 'disabled';
export type CustomerType = 'lead' | 'ecommerce' | 'publisher' | 'unclear';
export type ConsentMode = 'advanced' | 'basic' | 'none' | 'unclear' | 'misconfigured';
export type ScanEventStatus = 'active' | 'disabled' | 'vanished';
export type TargetStatus = 'active' | 'disabled' | 'vanished';
export type SimMethod = 'datalayer_push' | 'navigation' | 'synthetic_push' | 'unclear';
export type FocusSetBy = 'customer' | 'mso' | 'discovery';

/** Die drei Anlege-Ebenen (PRD Kap. 3). */
export const CREATE_LEVELS = [
  { level: 1, key: 'plain', label: 'Plain anlegen', hint: 'Domain, Einstellungen und Workspace-Referenz eintragen — sonst nichts.' },
  { level: 2, key: 'gtm', label: 'GTM auslesen', hint: 'Zusätzlich den GTM-Container parsen und das Scanprofil direkt schreiben.' },
  { level: 3, key: 'discovery', label: 'Discovery Run', hint: 'Zusätzlich die komplette Seitenanalyse anstoßen — läuft asynchron im Hintergrund.' },
] as const;

export type CreateLevel = 1 | 2 | 3;

export interface CreateDomainRequest {
  hostname: string;
  display_name?: string | null;
  workspace_ref?: string | null;
  level: CreateLevel;
  /** ambix-Wizard: die gewaehlten Fokus-Events (Event-Namen), nur mit Ebene 2 oder 3. */
  focus_events?: string[] | null;
}

export interface ProfileWriteSummary {
  events_written: number;
  targets_written: number;
  unclear_events: number;
  events_vanished: number;
  /** Re-Discovery: notierte Aenderungen zur Pruefung (seit 12.10.2026). */
  changes_recorded?: number;
}

export interface CreateDomainResponse {
  domain_id: string;
  hostname: string;
  level: CreateLevel;
  onboarding_status: OnboardingStatus;
  container?: { id: string | null; version: string | null };
  profile?: ProfileWriteSummary;
  task_name?: string;
  dispatch_error?: string;
  /** Ebene 3: welcher Weg genutzt wurde. */
  discovery_mode?: 'cloud_tasks' | 'direct';
  /** Die Fokus-Events, die beim Anlegen gesetzt wurden. */
  focus_events?: string[];
}

/**
 * Eine Auswahlmöglichkeit für die Workspace-Referenz (GET /workspace-refs).
 * `ref` ist der gespeicherte Wert (ambix-Workspace-Id als String), `name` der
 * Klartext aus dem MDM-Replikat `rep_workspace` — null, wenn das Replikat in
 * dieser Umgebung nicht lesbar ist oder die Referenz dort unbekannt ist
 * (Altbestand mit Slugs wie „mso-digital").
 */
export interface WorkspaceRefOption {
  ref: string;
  name: string | null;
  source: 'mdm' | 'domains';
}

export interface DomainListItem {
  id: string;
  hostname: string;
  display_name: string | null;
  workspace_ref: string | null;
  /** Klartext zur Referenz aus `rep_workspace`; null, wenn unbekannt oder nicht lesbar. */
  workspace_name: string | null;
  onboarding_status: OnboardingStatus | null;
  domain_status: DomainStatus | null;
  gtm_container_id: string | null;
  container_version: string | null;
  cmp_name: string | null;
  customer_type: CustomerType | null;
  consent_mode: ConsentMode | null;
  event_count: number;
  target_count: number;
  created_at: string;
}

export interface EventTargetDetail {
  id: string;
  channel_fk: string;
  channel_id: string | null;
  conversion_label: string | null;
  channel_info: Record<string, unknown> | null;
  event_target_status: TargetStatus;
  channel?: { channel: string; display_name: string; channel_category: string; id_name: string } | null;
}

export interface ScanEventDetail {
  id: string;
  event_name: string;
  event_type_fk: string;
  scan_event_status: ScanEventStatus;
  sim_method: SimMethod;
  /** true = Requests beobachten und blocken (Standard), false = durchlassen. */
  block_requests: boolean;
  test_url: string | null;
  event_payload: Record<string, unknown> | null;
  trigger_conditions: string[] | null;
  focus_event: boolean;
  focus_set_by: FocusSetBy | null;
  focus_set_at: string | null;
  targets: EventTargetDetail[];
  event_type?: { event_type: string; display_name: string } | null;
}

/* ── FR-1: Editieren im Expertenmodus ────────────────────── */

export interface UpdateDomainRequest {
  hostname?: string;
  display_name?: string | null;
  workspace_ref?: string | null;
}

export interface UpdateDomainSettingsRequest {
  onboarding_status?: OnboardingStatus;
  domain_status?: DomainStatus;
  gtm_container_id?: string | null;
  container_version?: string | null;
  customer_type?: CustomerType;
  cmp_name?: string | null;
  consent_mode?: ConsentMode;
}

export interface UpdateScanEventRequest {
  event_name?: string;
  event_type_fk?: string;
  scan_event_status?: ScanEventStatus;
  sim_method?: SimMethod;
  test_url?: string | null;
  focus_event?: boolean;
  block_requests?: boolean;
}

/** Antwort auf einen erneut gestarteten Discovery Run. */
export interface RerunDiscoveryResponse {
  domain_id: string;
  hostname: string;
  discovery_mode: 'cloud_tasks' | 'direct';
  onboarding_status: OnboardingStatus;
  task_name?: string;
}

export interface UpdateEventTargetRequest {
  channel_fk?: string;
  channel_id?: string | null;
  conversion_label?: string | null;
  event_target_status?: TargetStatus;
}

/* ── FR-2: Graph ─────────────────────────────────────────── */

export interface GraphTarget {
  id: string;
  channel: string;
  channel_display: string;
  channel_id: string | null;
  conversion_label: string | null;
  status: TargetStatus;
  /** Status nach logischer Vererbung von Domain und Event — nie gespeichert. */
  effective_status: TargetStatus | DomainStatus;
}

export interface GraphEvent {
  id: string;
  event_name: string;
  event_type: string;
  status: ScanEventStatus;
  effective_status: ScanEventStatus | DomainStatus;
  sim_method: SimMethod;
  focus_event: boolean;
  focus_set_by: FocusSetBy | null;
  targets: GraphTarget[];
}

export interface GraphDomain {
  id: string;
  hostname: string;
  display_name: string | null;
  domain_status: DomainStatus;
  onboarding_status: OnboardingStatus | null;
  gtm_container_id: string | null;
  cmp_name: string | null;
  events: GraphEvent[];
}

export interface GraphWorkspace {
  workspace_ref: string | null;
  /** Klartext aus `rep_workspace`; null, wenn unbekannt oder nicht lesbar. */
  workspace_name: string | null;
  domains: GraphDomain[];
}

export interface DomainDetail {
  id: string;
  hostname: string;
  display_name: string | null;
  workspace_ref: string | null;
  created_at: string;
  updated_at: string;
  settings: {
    id: string;
    onboarding_status: OnboardingStatus;
    domain_status: DomainStatus;
    gtm_container_id: string | null;
    container_version: string | null;
    customer_type: CustomerType;
    cmp_name: string | null;
    consent_mode: ConsentMode;
  } | null;
  scan_events: ScanEventDetail[];
}

/* ── FR-7: Job-Manager ───────────────────────────────────── */

export type JobStatus = 'pending' | 'running' | 'completed' | 'failed';
export type JobRunType = 'night_scan' | 'manual';

/**
 * Patch 01 (20.08.2026): EINE Jobtabelle fuer beide Arten.
 *   · raw_scan → der Scan-Lauf; `run_ref` ist sein ERGEBNIS
 *   · scoring  → die Bewertung; `run_ref` ist ihr GEGENSTAND
 */
export type JobKind = 'raw_scan' | 'scoring';

export const JOB_STATUS_ORDER: JobStatus[] = ['pending', 'running', 'completed', 'failed'];
export const JOB_KIND_ORDER: JobKind[] = ['raw_scan', 'scoring'];

/** Eine Zeile der Jobtabelle — `hostname` kommt denormalisiert aus dem Join. */
export interface JobListItem {
  id: string;
  domain_fk: string;
  hostname: string | null;
  workspace_ref: string | null;
  job_kind: JobKind;
  run_type: JobRunType;
  job_status: JobStatus;
  attempts: number;
  run_ref: string | null;
  error_detail: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

export interface JobListResponse {
  db_profile: string;
  /** Zaehler ueber beide Job-Arten zusammen. */
  counts: Record<JobStatus, number>;
  /** Dieselben Zaehler getrennt je Job-Art (PRD Kap. 2.1, Patch 01 Kap. 6). */
  counts_by_kind: Record<JobKind, Record<JobStatus, number>>;
  jobs: JobListItem[];
}

/**
 * Ein bewertbarer Raw-Lauf fuer den Trigger „Check and Score starten".
 * Kommt aus GET /api/watchdog/runs (BigQuery-Lesepfad).
 */
export interface ScanRunOption {
  run_id: string;
  run_at: string;
  domain_ref: string;
  hostname: string | null;
  run_status: string | null;
}

/* ── FR-5: Notification-Center ───────────────────────────── */

export type NotificationStatus = 'new' | 'confirmed' | 'false_alarm';
export type NotificationSeverity = 'showstopper' | 'warning' | 'notice';

/**
 * `question` und `explanation` kommen per Join aus twd_scoring_definitions und
 * koennen fehlen, wenn eine Definition nachtraeglich entfernt wurde.
 */
export interface NotificationListItem {
  id: string;
  domain_fk: string;
  hostname: string | null;
  run_ref: string;
  scoring_code: string;
  /** Ziel eines Ziel-Checks (seit 12.10.2026); null bei Domain-Checks und aelteren Meldungen. */
  target_ref?: string | null;
  /** Lesbarer Kontext des Ziels: „form_sent · meta · Kontaktformular gesendet". */
  target_context?: string | null;
  question: string | null;
  explanation: string | null;
  category: string | null;
  severity: NotificationSeverity;
  notification_status: NotificationStatus;
  decided_by: string | null;
  decided_at: string | null;
  /** Wann derselbe Check fuer dasselbe Ziel wieder bestanden hat; null = Vorfall offen. */
  resolved_at?: string | null;
  created_at: string;
}

/**
 * Das Scoring-Result aus BigQuery — hier stehen die Ist/Soll-Werte des Befunds.
 * Wird ueber run_ref + scoring_code zur Notification dazugeholt und kann fehlen,
 * wenn BigQuery nicht erreichbar ist oder der Lauf zu alt wurde.
 */
export interface NotificationScoringResult {
  run_ref: string;
  run_at: string | null;
  domain_ref: string | null;
  hostname: string | null;
  scoring_code: string;
  scope: string;
  event_ref: string | null;
  target_ref: string | null;
  result: string;
  severity: string;
  category: string;
  compared_with_run_ref: string | null;
  details: unknown | null;
}

export interface NotificationListResponse {
  db_profile: string;
  counts: Record<NotificationStatus, number>;
  notifications: NotificationListItem[];
}

export interface NotificationDetailResponse {
  db_profile: string;
  notification: NotificationListItem;
  /** Ist/Soll-Werte aus BigQuery. Null, wenn zum Lauf kein Ergebnis (mehr) vorliegt. */
  result: NotificationScoringResult | null;
  /** Der zugehoerige Scanbericht aus BigQuery — nur bei `?include=run`. */
  run: Record<string, unknown> | null;
  /** Gesetzt, wenn der Scanbericht nicht geladen werden konnte. Die Seite bleibt trotzdem bedienbar. */
  run_error: string | null;
}
